import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

// const DELETION_EMAIL = "saransai.b@varsitymgmt.com";

type FormData = {
  fullName: string;
  email: string;
  organization: string;
  statement: string;
};

const initialFormData: FormData = {
  fullName: "",
  email: "",
  organization: "",
  statement: "",
};

function DataDeletion() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormData, string>>
  >({});

  const validate = (): boolean => {
    const nextErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.fullName.trim()) {
      nextErrors.fullName = "Full name is required.";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address.";
    }

    if (!formData.statement.trim()) {
      nextErrors.statement =
        "Please provide a brief deletion request statement.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validate()) return;

    const subject = encodeURIComponent(
      "Data Deletion Request - Sri Chaitanya CRM"
    );

    const body = encodeURIComponent(
      [
        "Data Deletion Request",
        "",
        `Full Name: ${formData.fullName.trim()}`,
        `Email: ${formData.email.trim()}`,
        `Organization: ${formData.organization.trim()}`,
        "",
        "Request Statement:",
        formData.statement.trim(),
      ].join("\n")
    );

    setSubmitted(true);

    // subject;
    // body;
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setErrors({});
    setSubmitted(false);
  };

  return (
    <div className="container py-5">
      <div className="card shadow-sm border-0">
        <div className="card-body p-4 p-md-5">
          <h1 className="border-bottom pb-3 mb-4">
            Data Deletion Request
          </h1>

          <p>
            If you are a user of <strong>Sri Chaitanya CRM</strong>, you have
            the right to request deletion of your account and all associated
            personal data.
          </p>

          <h2 className="h4 mt-4">What Gets Deleted</h2>

          <p>
            Upon a verified deletion request, we will remove the following data
            associated with your account:
          </p>

          <ul className="ps-4">
            <li>Your user profile and account credentials</li>
            <li>Profile photos uploaded by you</li>
            <li>Login activity and audit logs linked to your account</li>
            <li>Any personal information stored under your user record</li>
          </ul>

          <h2 className="h4 mt-4">Data That May Be Retained</h2>

          <p>
            Certain data may be retained where required by law, for legitimate
            business purposes, or when entered as part of organizational records
            (such as customer interaction notes created in the course of
            business). Retained data is handled in accordance with our Privacy
            Policy and applicable regulations.
          </p>

          <h2 className="h4 mt-4">Request Deletion</h2>

          <p>
            Fill in the form below with your account details. After submission,
            your email client will open with a pre-filled message to send to our
            team.
          </p>

          {submitted ? (
            <div
              className="alert alert-success d-flex flex-column gap-3"
              role="alert"
            >
              <p className="mb-0">
                <strong>Request prepared.</strong> Your email client should open
                with your deletion request. If it did not open, please email{" "}
                {/* <a href={`mailto:${DELETION_EMAIL}`}>
                  {DELETION_EMAIL}
                </a>{" "} */}
                with the same details.
              </p>

              <div>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleReset}
                >
                  Submit another request
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>

              <div className="mb-3">
                <label htmlFor="fullName" className="form-label">
                  Full name
                </label>

                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  className={`form-control ${
                    errors.fullName ? "is-invalid" : ""
                  }`}
                  autoComplete="name"
                  value={formData.fullName}
                  onChange={handleChange}
                />

                {errors.fullName && (
                  <div className="invalid-feedback">
                    {errors.fullName}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  Email address (CRM account)
                </label>

                <input
                  id="email"
                  name="email"
                  type="email"
                  className={`form-control ${
                    errors.email ? "is-invalid" : ""
                  }`}
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                />

                {errors.email && (
                  <div className="invalid-feedback">
                    {errors.email}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="organization" className="form-label">
                  Organization name
                </label>

                <input
                  id="organization"
                  name="organization"
                  type="text"
                  className={`form-control ${
                    errors.organization ? "is-invalid" : ""
                  }`}
                  autoComplete="organization"
                  value={formData.organization}
                  onChange={handleChange}
                />

                {errors.organization && (
                  <div className="invalid-feedback">
                    {errors.organization}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="statement" className="form-label">
                  Deletion request statement
                </label>

                <textarea
                  id="statement"
                  name="statement"
                  rows={4}
                  className={`form-control ${
                    errors.statement ? "is-invalid" : ""
                  }`}
                  placeholder="Briefly confirm that you want your account and associated personal data deleted."
                  value={formData.statement}
                  onChange={handleChange}
                />

                {errors.statement && (
                  <div className="invalid-feedback">
                    {errors.statement}
                  </div>
                )}
              </div>

              <button type="submit" className="btn btn-primary">
                Submit deletion request
              </button>
            </form>
          )}

          <h2 className="h4 mt-5">Processing Time</h2>

          <p>
            We will verify your identity and process your deletion request
            within a reasonable timeframe. You will receive confirmation once
            your data has been deleted.
          </p>

          <h2 className="h4 mt-4">Questions</h2>

          <p>
            For any questions about data deletion or your privacy rights,
            please refer to our{" "}
            <Link to="/privacy-policy">Privacy Policy</Link> or contact us at{" "}
            {/* <a href={`mailto:${DELETION_EMAIL}`}>
              {DELETION_EMAIL}
            </a>. */}
          </p>
        </div>
      </div>
    </div>
  );
}

export default DataDeletion;